import { useEffect, useState } from 'react';
import { Session } from 'src/models/Session';
import { FilterTrait } from 'src/models/SessionFilters';
import { SessionsService } from 'src/services';
import type { PlaylistContentType } from './api';
import {
  playlistContentTypeLabel,
  resolvePlaylistContentType,
} from './playlistPaths';

const DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 2;

export type PlaylistSearchOption = {
  key: string;
  contentId: string;
  contentType: PlaylistContentType;
  title: string;
  subtitle: string;
  thumbnailUrl: string | null;
  typeLabel: string;
};

function sessionThumbnail(session: Session): string | null {
  if (session.horizontalThumbnail) return session.horizontalThumbnail;
  const thumbs = session.video?.thumbnail;
  if (!thumbs?.length) return null;
  const best = thumbs.reduce(
    (acc, t) => (t.width > acc.width ? t : acc),
    thumbs[0]!,
  );
  return best.link;
}

function mapSession(session: Session): PlaylistSearchOption {
  const contentType = resolvePlaylistContentType(session);
  return {
    key: `${contentType}:${session.id}`,
    contentId: session.id,
    contentType,
    title: session.title,
    subtitle: session.underline || session.coach.map(c => c.name).join(', ') || '',
    thumbnailUrl: sessionThumbnail(session),
    typeLabel: playlistContentTypeLabel(contentType),
  };
}

export function usePlaylistContentSearch(query: string, enabled = true) {
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [options, setOptions] = useState<PlaylistSearchOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedQuery(query.trim()),
      DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!enabled || debouncedQuery.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    void SessionsService.getMany({
      limit: 15,
      filters: [{ trait: FilterTrait.Phrase, value: debouncedQuery }],
    })
      .then((sessionsRes) => {
        if (cancelled) return;
        setOptions(sessionsRes.data.map(mapSession));
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, enabled]);

  return { options, isLoading };
}
